import React, {FunctionComponent, useState} from "react";
import BookingItemInformation from "../../booking/BookingItemInformation";
import {
  BookingDocumentData,
  BookingDocumentFormData,
  BookingStatus,
  InvoiceFileDetails
} from "../../../../interfaces/BookingInterfaces";
import FieldLabel from "../../../atoms/FieldLabel";
import {FormattedMessage, useIntl} from "react-intl";
import BookingDocumentFormCard from "./BookingDocumentFormCard";
import {Formik} from "formik";
import EpowForm from "../EpowForm";
import {GET_BOOKING_DOCUMENT_FORM_CATEGORIES} from "../../../../constants/booking/Booking";
import {bookingService} from "../../../../services/BookingService";
import {toastUtils} from "../../../../utils/toastUtils";
import {BookingDocumentFormValidationSchema} from "../../../../constants/validation/BookingValidationSchemas";
import FormInput from "../../input/FormInput";
import {ColorType} from "../../../../types/bootstrap/BootstrapType";
import Button from "../../../atoms/Button";
import {orderUtils} from "../../../../utils/orderUtils";
import ModalItemsToPay from "../../modal/ModalItemsToPay";
import {fileUtils} from "../../../../utils/fileUtils";
import {ApiError} from "../../../../interfaces/ErrorInterfaces";

interface BookingDocumentFormProps {
  id?: string
  className?: string;
  bookingDocumentData: BookingDocumentData;
  onValidate?: () => void
  onRefreshBookingDetails?: () => void
  onDeleteDocument?: (documentId: string) => void
  onCancel?: () => void
  onKeep?: () => void
}

const BookingDocumentForm: FunctionComponent<BookingDocumentFormProps> = ({
  id,
  className = "",
  bookingDocumentData,
  onValidate,
  onRefreshBookingDetails,
  onDeleteDocument,
  onCancel,
  onKeep,
}) => {
  const intl = useIntl();
  const [payModalOpened, setPayModalOpened] = useState<boolean>(false);
  const [missingReference, setMissingReference] = useState<boolean>(false);

  const categories = GET_BOOKING_DOCUMENT_FORM_CATEGORIES(bookingDocumentData.isPaid, () => setPayModalOpened(true));

  const initialDocumentDetails = bookingDocumentData?.billDocuments?.reduce((acc, doc) => {
    acc[doc.fileId] = {
      invoiceNumber: doc.fileDetails || "",
      invoiceDate: doc.invoiceDate || ""
    };
    return acc;
  }, {} as Record<string, InvoiceFileDetails>);

  const initialValues: BookingDocumentFormData = {
    isBilled: bookingDocumentData?.isBilled,
    isPaid: bookingDocumentData?.isPaid,
    isConfirmed: bookingDocumentData?.supportingDocuments?.length > 0 || bookingDocumentData.status === BookingStatus.DOCUMENT_RECEIVED,
    paymentDate: bookingDocumentData?.paymentDate,
    updateEmailContent: "", // todo : set initial value once received
    reference: bookingDocumentData?.reference,
    fileDetails: initialDocumentDetails,
    atLeastOneBillFile: bookingDocumentData?.billDocuments?.length > 0,
    atLeastOnePaymentFile: bookingDocumentData?.paymentDocuments?.length > 0,
    directDocuments: {},
  }

  const payBookings = () => {
    bookingService.generateSepaFile([bookingDocumentData?.id])
      .then((file) => {
        toastUtils.successToast(intl.formatMessage({id: "booking_pay_modal_success"}))
        fileUtils.saveFile(file, "bookings_sepa.xml")
        setPayModalOpened(false)
        onValidate && onValidate()
      }).catch((error: ApiError) => {
      if (error && error.message) {
        toastUtils.errorToast(error.message)
      } else {
        toastUtils.errorToast(intl.formatMessage({id: "booking_pay_modal_failure"}))
      }
    })
  }

  const handleSubmit = (values: BookingDocumentFormData, { resetForm }: { resetForm: () => void }) => {
    if (!bookingDocumentData?.id) return

    bookingService.updatePendingBooking(bookingDocumentData?.id, values)
      .then((response) => {
        if (response.status === BookingStatus.CLOSED) {
          toastUtils.successToast(intl.formatMessage({id: "success_toast_booking_closed"}));
        } else if (response.missingReferenceForClosure) {
          toastUtils.warningToast(intl.formatMessage({id: "booking_reference_required_error"}));
          setMissingReference(true);
          onRefreshBookingDetails && onRefreshBookingDetails();
          return;
        } else {
          toastUtils.successToast(intl.formatMessage({id: "success_toast_update_booking_document"}));
        }
        setMissingReference(false);
        resetForm();
        onValidate && onValidate()
      })
      .catch(() => {
        toastUtils.errorToast(intl.formatMessage({id: "error_toast_update_booking_document"}))
      })
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={BookingDocumentFormValidationSchema}
        onSubmit={handleSubmit}>
        {({ errors, touched, values, setFieldValue, resetForm }) => (
          <EpowForm id={id}>
            <div className={`w-100 d-flex flex-column flex-shrink gap-3 mb-5 ${className}`}>
              <FieldLabel
                label={intl.formatMessage({id: "booking_form_client_order"})}
                value={orderUtils.getOrderRefDisplay(bookingDocumentData?.orderCode, bookingDocumentData?.orderRef, bookingDocumentData?.orderCustomerName)}
              />
              <BookingItemInformation date={bookingDocumentData?.date} hour={bookingDocumentData?.hour} pax={bookingDocumentData?.pax}/>
              <div className="d-flex gap-3">
                <div className="d-flex gap-5">
                  <FieldLabel
                    label={intl.formatMessage({id: "booking_pax"})}
                    value={bookingDocumentData?.pax}
                  />
                  <FieldLabel
                    label={intl.formatMessage({id: "purchase_booking_quantity"})}
                    value={bookingDocumentData?.quantity}
                  />
                </div>
              </div>
              <FieldLabel label={intl.formatMessage({id: "booking_form_purchase_order"})} value={bookingDocumentData?.purchaseNumber} />
              <FieldLabel label={intl.formatMessage({id: "SUPPLIER"})} value={bookingDocumentData?.defaultSupplierName} />
              <FieldLabel label={intl.formatMessage({id: "booking_service"})} value={bookingDocumentData?.serviceName} />
              <FormInput
                id="reference"
                label="booking_reference"
                value={values?.reference}
                onChange={(e) => setFieldValue("reference", e.target?.value)}
                disabled={bookingDocumentData?.status === BookingStatus.CLOSED}
              />
              {(missingReference || bookingDocumentData?.missingReferenceForClosure) && !values?.reference && (
                <div className="epow-error">
                  <FormattedMessage id="booking_reference_required_error" />
                </div>
              )}

              {categories.map(category =>
                <BookingDocumentFormCard
                  key={category.title}
                  category={category}
                  bookingDocumentData={bookingDocumentData}
                  errors={errors}
                  touched={touched}
                  values={values}
                  onChangeValue={setFieldValue}
                  onDeleteDocument={onDeleteDocument}
                  onRefreshBookingDetails={onRefreshBookingDetails}
                />
              )}
              {(bookingDocumentData?.status) === BookingStatus.TO_CANCEL &&
                <div className="d-flex justify-content-start mb-2">
                  <Button
                      color={ColorType.DANGER}
                      className="border border-danger"
                      onClick={() => {
                        resetForm();
                        onCancel && onCancel();
                      }}>
                      <FormattedMessage id="booking_form_cancel"/>
                  </Button>

                  <Button
                    className="border border-primary ms-2"
                    onClick={() => {
                      resetForm();
                      onKeep && onKeep();
                    }}>
                    <FormattedMessage id="booking_form_keep"/>
                  </Button>
                </div>
              }
            </div>
          </EpowForm>
        )}
      </Formik>
    <ModalItemsToPay
      opened={payModalOpened}
      setOpen={setPayModalOpened}
      action={payBookings}
      title={intl.formatMessage({id: "booking_pay_modal_title"})}
      textContent={intl.formatMessage({id: "booking_pay_modal_body"})}
    />
  </>
  )
}

export default BookingDocumentForm;
